import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { LANGS } from "@/lib/translations";
import api from "@/lib/api";
import { applyTheme, saveThemeToAccount, currentTheme } from "@/lib/theme";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Sun, Moon, Monitor } from "lucide-react";

// On login: adopt the account's saved theme, or — for a brand-new account with no
// preference yet — ask once which they'd like and remember it on their account.
export default function ThemePrompt() {
  const { user } = useAuth();
  const { setLang } = useI18n();
  const [open, setOpen] = useState(false);
  const checked = useRef(false);

  useEffect(() => {
    if (!user) { checked.current = false; return; }
    if (checked.current) return;
    checked.current = true;
    api.get("/profile").then(({ data }) => {
      if (data && LANGS.some((l) => l.code === data.lang) && !/^\/(es|fr|ar|hmn)\//.test(window.location.pathname)) setLang(data.lang);
      if (data && (data.theme === "light" || data.theme === "dark" || data.theme === "auto")) applyTheme(data.theme);
      else setOpen(true);
    }).catch(() => {});
  }, [user, setLang]);

  const choose = (theme) => { applyTheme(theme); saveThemeToAccount(theme); setOpen(false); };
  const onOpenChange = (o) => { if (!o) { saveThemeToAccount(currentTheme()); setOpen(false); } };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-md" data-testid="theme-prompt">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Choose your look</DialogTitle>
          <DialogDescription>Pick the color scheme you prefer — you can change it anytime, and we'll remember it on your account.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-3 mt-2">
          <button onClick={() => choose("light")} data-testid="theme-prompt-light"
            className="flex flex-col items-center gap-2 p-5 border-2 border-border hover:border-accent transition-colors">
            <span className="grid place-items-center w-11 h-11 bg-slate-100 text-slate-700 border-2 border-slate-300"><Sun size={20} /></span>
            <span className="text-sm font-semibold">Light</span>
          </button>
          <button onClick={() => choose("dark")} data-testid="theme-prompt-dark"
            className="flex flex-col items-center gap-2 p-5 border-2 border-border hover:border-accent transition-colors">
            <span className="grid place-items-center w-11 h-11 bg-slate-900 text-slate-100 border-2 border-slate-700"><Moon size={20} /></span>
            <span className="text-sm font-semibold">Dark</span>
          </button>
          <button onClick={() => choose("auto")} data-testid="theme-prompt-auto"
            className="flex flex-col items-center gap-2 p-5 border-2 border-border hover:border-accent transition-colors">
            <span className="grid place-items-center w-11 h-11 bg-gradient-to-br from-slate-100 to-slate-900 text-slate-500 border-2 border-slate-400"><Monitor size={20} /></span>
            <span className="text-sm font-semibold">Auto</span>
          </button>
        </div>
        <p className="text-xs text-muted-foreground text-center mt-1">Auto follows your device's light/dark setting.</p>
      </DialogContent>
    </Dialog>
  );
}
